import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import type { OptionBoxOption } from '../../lease-details.types';

type CompanionFields = Record<string, (option: OptionBoxOption) => unknown>;

type UseOptionBoxSelectionSyncParams = {
  name: string;
  value: string;
  selectedId: string | undefined;
  options: OptionBoxOption[];
  isSettled: boolean;
  companionIdField?: string;
  companionFields?: CompanionFields;
  clearDisabledSelection: boolean;
};

// Prefer the id (options can share a value), then fall back to the value.
export const findSelectedOption = (
  options: OptionBoxOption[],
  value: string,
  selectedId: string | undefined,
): OptionBoxOption | undefined =>
  (selectedId ? options.find((option) => option.id === selectedId) : undefined) ??
  (value ? options.find((option) => option.value === value) : undefined);

const isBlank = (value: unknown) => value === null || value === undefined || value === '';

const isSameValue = (a: unknown, b: unknown) =>
  a === b || JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/**
 * Keeps an option-box selection consistent with its options once they settle (never mid-load, so
 * a refetch can't wipe a selection):
 *  - the selection is gone (or disabled, when opted in) → clear it and its companions, so the user
 *    never submits a choice that isn't on screen;
 *  - it's still there → re-sync the companions, since a reload can carry new data (e.g. pricing
 *    after a rent change) for the same option.
 */
export const useOptionBoxSelectionSync = ({
  name,
  value,
  selectedId,
  options,
  isSettled,
  companionIdField,
  companionFields,
  clearDisabledSelection,
}: UseOptionBoxSelectionSyncParams) => {
  const { setValue, getValues } = useFormContext();

  useEffect(() => {
    const companions = Object.entries(companionFields ?? {});
    // No selection (e.g. another field's reset cleared it): nothing may keep the previous
    // option's data. Already-blank companions are left alone so mount writes nothing.
    if (!value) {
      companions.forEach(([fieldName]) => {
        if (!isBlank(getValues(fieldName))) {
          setValue(fieldName, null, { shouldDirty: false });
        }
      });
      return;
    }
    if (!isSettled) {
      return;
    }
    const selected = findSelectedOption(options, value, selectedId);
    if (!selected || (clearDisabledSelection && selected.disabled)) {
      setValue(name, '', { shouldValidate: true });
      if (companionIdField) {
        setValue(companionIdField, '', { shouldDirty: false });
      }
      companions.forEach(([fieldName]) => setValue(fieldName, null, { shouldDirty: false }));
      return;
    }
    companions.forEach(([fieldName, derive]) => {
      const next = derive(selected);
      if (!isSameValue(getValues(fieldName), next)) {
        setValue(fieldName, next, { shouldDirty: false });
      }
    });
  }, [
    isSettled,
    value,
    selectedId,
    options,
    name,
    companionIdField,
    companionFields,
    clearDisabledSelection,
    setValue,
    getValues,
  ]);
};
